import { cn } from "@getgreenline/blaze-ui";
import { useFormikContext } from "formik";
import { adFormatLabels } from "@/app-config";
import { useMarketsCatalog } from "@/hooks/use-markets-catalog";
import { usePlacementCatalog } from "@/hooks/use-placement-catalog";
import { type CampaignFormValues } from "../campaign-form";
import { PlacementChannelSection } from "../cards/placement-channel-section";
import { CampaignContextPill } from "../../shared/campaign-context-pill";
import { StepAlert } from "../../shared/step-alert";
import { StepHeader } from "../../shared/step-header";

type PlacementStepProps = {
  errorMessage?: string;
  errorField?: string;
  showError?: boolean;
  onDismissError: () => void;
};

export function PlacementStep({
  errorMessage,
  errorField,
  showError = false,
  onDismissError,
}: PlacementStepProps) {
  const { values, setFieldValue } = useFormikContext<CampaignFormValues>();
  const { data: channels = [], isLoading, error } = usePlacementCatalog();
  const selectedBrand = values.brand;
  const { data: states = [] } = useMarketsCatalog(selectedBrand?.country);
  const selectedState = states.find(
    (state) => state.code === values.marketState,
  )?.name;

  return (
    <section className="tw:max-w-5xl">
      <StepHeader
        title="Placement"
        description="Select which Blaze-powered channels carry your campaign and configure the ad zones within each one."
      />

      <CampaignContextPill
        brandName={selectedBrand?.name}
        details={[selectedState || adFormatLabels[values.adFormat] || "Market"]}
      />

      <StepAlert
        show={showError}
        message={errorMessage}
        onDismiss={onDismissError}
      />

      {isLoading ? (
        <p className="tw:mt-8 tw:text-lg tw:text-muted-foreground">
          Loading placements...
        </p>
      ) : error ? (
        <p className="tw:mt-8 tw:text-lg tw:text-destructive">
          Unable to load placements.
        </p>
      ) : (
        <div
          className={cn(
            "tw:mt-4 tw:space-y-3",
            showError &&
              (errorField === "placementChannels" ||
                errorField === "placementZones") &&
              "tw:rounded-xl tw:border tw:border-destructive/40 tw:bg-destructive/5 tw:p-3",
          )}
        >
          {channels.map((channel) => {
            const selectedZoneIds = channel.zones
              .filter((zone) =>
                values.placementZones.includes(`${channel.id}.${zone.id}`),
              )
              .map((zone) => zone.id);

            return (
              <PlacementChannelSection
                key={channel.id}
                channel={channel}
                enabled={values.placementChannels.includes(channel.id)}
                selectedZoneIds={selectedZoneIds}
                onEnabledChange={(enabled) => {
                  if (enabled) {
                    setFieldValue("placementChannels", [
                      ...values.placementChannels,
                      channel.id,
                    ]);
                    return;
                  }

                  setFieldValue(
                    "placementChannels",
                    values.placementChannels.filter(
                      (channelId) => channelId !== channel.id,
                    ),
                  );
                  setFieldValue(
                    "placementZones",
                    values.placementZones.filter(
                      (zoneId) =>
                        !channel.zones.some(
                          (zone) => `${channel.id}.${zone.id}` === zoneId,
                        ),
                    ),
                  );
                }}
                onZoneToggle={(zoneId, checked) => {
                  if (checked) {
                    setFieldValue("placementZones", [
                      ...values.placementZones,
                      `${channel.id}.${zoneId}`,
                    ]);
                    return;
                  }

                  setFieldValue(
                    "placementZones",
                    values.placementZones.filter(
                      (selectedZoneId) =>
                        selectedZoneId !== `${channel.id}.${zoneId}`,
                    ),
                  );
                }}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}
